import { useEffect, useRef } from 'react';
import { usePotionUIControls } from './use-potion-ui-controls';
import { useAppStore } from '@/hooks/use-app-store';
import { usePotionVideos } from './use-potion-videos';
import { UI_TIMING } from '@/lib/timing-constants';
import { useUIEventQueue } from './use-ui-event-queue';

// Game end callback type
export interface GameEndData {
  gameWon: boolean;
  gamePhase: 'completed' | 'failed';
  potionAttempts: any[];
}

export type GameEndCallback = (data: GameEndData) => void;

// Create a simple event emitter for AttemptCounter updates
let attemptCounterUpdateCallback: ((attempt: number, maxAttempts: number) => void) | null = null;

export const setAttemptCounterCallback = (callback: (attempt: number, maxAttempts: number) => void) => {
  attemptCounterUpdateCallback = callback;
};

export const clearAttemptCounterCallback = () => {
  attemptCounterUpdateCallback = null;
};

export const useAIPotionUIBridge = (gameEndCallback?: GameEndCallback) => {
  const {controls, activeButton, buttonTexts, handleButtonToggle} = usePotionUIControls();
  const { sub, unsub, sendMessageWithContext } = useAppStore();
  const { playCauldronSmokeEffect, playScreenSmokeEffect, playPotionVideo } = usePotionVideos();
  const nextPhaseCallbackRef = useRef<(() => void) | null>(null);
  
  // Helper function for smooth button transitions
  const handleSmoothButtonTransition = (
    currentButton: 'button1' | 'button2' | 'button3',
    ingredient: string,
    nextButton?: 'button1' | 'button2' | 'button3' | 'complete'
  ) => {
    // Update button text immediately
    controls.updateButtonText(currentButton, ingredient);
    console.log(`Updated ${currentButton} text to: ${ingredient}`);
    
    // Play cauldron smoke effect immediately  
    playCauldronSmokeEffect();
    
    // Schedule deactivation after 2500ms (2.5 seconds)
    eventQueue.enqueue(`deactivate_${currentButton}`, 2500, () => {
      console.log(`🔘 Deactivating ${currentButton} after ingredient added`);
      
      // Deactivate current button by setting activeButton to null
      controls.deactivateAllButtons();
      
      // Activate next button after deactivation, if specified
      if (nextButton && nextButton !== 'complete') {
        console.log(`🔄 Activating ${nextButton} after ${currentButton} deactivated`);
        
        if (nextButton === 'button1') {
          controls.activateButton1();
        } else if (nextButton === 'button2') {
          controls.activateButton2();
        } else if (nextButton === 'button3') {
          controls.activateButton3();
        }
      } else if (nextButton === 'complete') {
        console.log('🎬 All buttons filled - sequence ready for completion');
      }
    });
  };
  const eventQueue = useUIEventQueue();


  //eventData.data.potionName <--- USE THIS TO DISPLAY THE POTION NAME TO THE FRONTEND
  
  useEffect(() => {
    // Handler for AI message completion tracking
    const handleMessage = (messageData: any) => {
      // Check if this is the final AI message (partial: false) and we have a pending callback
      if (messageData?.partial === false && nextPhaseCallbackRef.current) {
        console.log('🎤 AI message completed, scheduling next phase with 7s audio buffer...');
        
        // Wait 7 seconds for TTS audio to finish, then trigger next phase
        setTimeout(() => {
          if (nextPhaseCallbackRef.current) {
            nextPhaseCallbackRef.current();
            nextPhaseCallbackRef.current = null; // Clear the callback
          }
        }, 7000);
      }
    };

    // Handler for system events from the backend AI
    const handleSystemEvent = (eventData: any) => {
      
      // Check if it's a UI control event from the AI
      if (eventData?.data?.type === 'uiControl') {
        const { action, ingredient, buttonId, text, currentIngredientCount, totalIngredients } = eventData.data;
        
        switch (action) {
          case 'progress_to_button1':
            if (ingredient) {
              const ingredientObj = {
                name: ingredient,
                category: 'Herb/Fresh' as const,
                rawInput: ingredient
              };
              // 3rd ingredient -> button1, sequence complete
              handleSmoothButtonTransition('button1', ingredientObj.name, 'complete');
            }
            break;
            
          case 'progress_to_button2':
            if (ingredient) {
              const ingredientObj = {
                name: ingredient,
                category: 'Herb/Fresh' as const,
                rawInput: ingredient
              };
              // 1st ingredient -> button2, next activate button3
              handleSmoothButtonTransition('button2', ingredientObj.name, 'button3');
            }
            break;
            
          case 'progress_to_button3':
            if (ingredient) {
              const ingredientObj = {
                name: ingredient,
                category: 'Herb/Fresh' as const,
                rawInput: ingredient
              };
              // 2nd ingredient -> button3, next activate button1
              handleSmoothButtonTransition('button3', ingredientObj.name, 'button1');
            }
            break;
            
          // Note: Individual button activation cases removed - now handled by smooth transitions
          case 'activate_button1':
          case 'activate_button2':
          case 'activate_button3':
            console.log(`⚠️ Direct ${action} called - consider using smooth transitions instead`);
            if (action === 'activate_button1') controls.activateButton1();
            else if (action === 'activate_button2') controls.activateButton2();
            else if (action === 'activate_button3') controls.activateButton3();
            break;
            
          case 'deactivate_buttons':
            controls.deactivateAllButtons();
            break;
            
          case 'update_button_text':
            if (buttonId && text !== undefined) {
              // If text is empty, clear the button text
              const displayText = text || '';
              controls.updateButtonText(buttonId as 'button1' | 'button2' | 'button3', displayText);
              console.log(`Updated ${buttonId} text to: "${displayText}"`);
            }
            break;
            
          case 'complete_sequence':
            console.log('🎬 Complete sequence triggered - asking LLM to calculate potion result');
            // Ask the LLM to trigger the processPotionGameAction tool with complete_sequence
            sendMessageWithContext('Use processPotionGameAction tool with action: complete_sequence to calculate the final potion result.');
            break;
            
          case 'accept_ingredient_reluctantly':
            // Treat this exactly like a normal ingredient - let it follow the same flow
            if (ingredient && currentIngredientCount !== undefined) {
              console.log('accept_ingredient_reluctantly - treating as normal ingredient:', currentIngredientCount, 'ingredient:', ingredient);
              console.log('Backend total ingredients:', totalIngredients);
              
              let targetButton: 'button1' | 'button2' | 'button3' | null = null;
              let nextButton: 'button1' | 'button2' | 'button3' | 'complete' | undefined = undefined;
              
              // Use the ingredient count from backend instead of guessing from button states
              // Game flow: button2 (1st) -> button3 (2nd) -> button1 (3rd)
              if (currentIngredientCount === 1) {
                targetButton = 'button2'; // First ingredient goes to button2
                nextButton = 'button3';   // Next activate button3
              } else if (currentIngredientCount === 2) {
                targetButton = 'button3'; // Second ingredient goes to button3
                nextButton = 'button1';   // Next activate button1
              } else if (currentIngredientCount === 3) {
                targetButton = 'button1'; // Third ingredient goes to button1
                nextButton = 'complete';  // Sequence complete - trigger normal completion
              }
              
              console.log('Target button determined from backend count:', targetButton, 'for count:', currentIngredientCount);
              
              if (targetButton) {
                const ingredientObj = {
                  name: ingredient,
                  category: 'Herb/Fresh' as const,
                  rawInput: ingredient
                };
                
                // Use smooth transition instead of immediate activation
                handleSmoothButtonTransition(targetButton, ingredientObj.name, nextButton);
                
                // If this is the third ingredient, trigger complete_sequence just like normal ingredients
                if (currentIngredientCount === 3) {
                  console.log('🎬 Third ingredient (reluctant) - triggering complete_sequence like normal flow');
                  // Trigger the complete sequence immediately like normal ingredients do
                  sendMessageWithContext('Use processPotionGameAction tool with action: complete_sequence to calculate the final potion result.');
                }
              } else {
                console.error('Could not determine target button for ingredient:', ingredient, 'count:', currentIngredientCount);
              }
            } else {
              console.error('accept_ingredient_reluctantly missing data - ingredient:', ingredient, 'count:', currentIngredientCount);
            }
            break;
            
          default:
            console.warn('Unknown AI UI control action:', action);
        }
      }
      
      // Handle potion result events - CLEAN UI ORCHESTRATION!
      else if (eventData?.data?.type === 'potionResult') {
        const { result, potionName, isWinningPotion, videoShape, videoColor, gameCompletion, hints, attempt, gameWon, gamePhase } = eventData.data;
        console.log('🎬 Processing potion result event:', { result, potionName, isWinningPotion, videoShape, videoColor });
        console.log('🎮 Game completion data:', { gameCompletion, hints, attempt, gameWon, gamePhase });
        
        // Fix undefined attempt - calculate the attempt that just completed
        // If previousAttempts.length = 1, that means we just completed attempt 1
        const currentAttempt = attempt ?? gameCompletion?.previousAttempts?.length ?? 1;
        console.log('🔢 Attempt number - original:', attempt, 'calculated:', currentAttempt);
        console.log('🔍 GameCompletion details:', {
          previousAttemptsLength: gameCompletion?.previousAttempts?.length,
          previousAttempts: gameCompletion?.previousAttempts,
          gamePhase
        });
        
        // Log the potion name for frontend use
        if (potionName) {
          console.log('🧪 POTION NAME for frontend display:', potionName);
        }
        
        // IMPROVED SEQUENCE: Handle all timing with event queue instead of nested setTimeout
        console.log('🎭 Starting improved potion sequence with event queue...');
        
        // Cancel any existing potion sequence events to prevent conflicts
        eventQueue.cancelByAction('screen_smoke');
        eventQueue.cancelByAction('potion_video');
        eventQueue.cancelByAction('ai_commentary');
        eventQueue.cancelByAction('next_phase');
        
        // Step 1: Wait 5 seconds, then play screen smoke
        eventQueue.enqueue('screen_smoke', 5000, () => {
          console.log('💨 Playing screen smoke effect');
          playScreenSmokeEffect();
        });
        
        // Step 2: Wait 7 seconds total, then play potion video  
        eventQueue.enqueue('potion_video', 7000, () => {
          console.log(`🎬 Playing potion video: ${videoShape} ${videoColor}`);
          playPotionVideo(videoShape as any, videoColor as any);
        });
        
        // Step 3: Wait for video to finish, then trigger AI commentary
        eventQueue.enqueue('ai_commentary', 5000 + UI_TIMING.SCREEN_SMOKE_DURATION + UI_TIMING.POTION_VIDEO_DURATION, () => {
          console.log('🎭 Triggering AI commentary about potion');
          
          // NOW set up the callback for next phase - AFTER we start the commentary
          nextPhaseCallbackRef.current = () => {
            console.log('🔄 NEXT PHASE EVENT TRIGGERED by AI message completion!');
            console.log('🔍 Debug values:', { isWinningPotion, attempt, currentAttempt, gameWon });
            
            if (!isWinningPotion && currentAttempt < 3) {
              console.log(`🔄 Attempt ${currentAttempt} failed - clearing for attempt ${currentAttempt + 1}`);
              
              // Clear button texts
              console.log('🧹 Clearing button texts...');
              controls.updateButtonText('button1', '');
              controls.updateButtonText('button2', '');
              controls.updateButtonText('button3', '');
              
              // Deactivate all buttons
              console.log('🔘 Deactivating all buttons...');
              controls.deactivateAllButtons();
              
              // Tell the backend what attempt we should be starting
              console.log(`📤 Sending message to start attempt ${currentAttempt + 1} (after completing ${currentAttempt})...`);
              sendMessageWithContext(`Start attempt ${currentAttempt + 1} of 3 total attempts. Previous attempt ${currentAttempt} was completed. Use the uiControl system to activate the first ingredient button for the new attempt.`);
              
              // Update the AttemptCounter UI to show the next attempt
              if (attemptCounterUpdateCallback) {
                console.log(`🎯 Updating AttemptCounter to show attempt ${currentAttempt + 1}/3`);
                attemptCounterUpdateCallback(currentAttempt + 1, 3);
              }
              
              console.log(`✅ UI ready for attempt ${currentAttempt + 1}/3`);
            } else if (!isWinningPotion && currentAttempt >= 3) {
              console.log('💀 GAME OVER - All attempts used, triggering lose scenario');
              
              // Trigger lose scenario with screen smoke transition
              setTimeout(() => {
                console.log('🎭 Playing screen smoke for game over transition');
                playScreenSmokeEffect();
                
                // After smoke effect, trigger lose scenario
                setTimeout(() => {
                  console.log('📤 Triggering lose scenario event');
                  if (gameEndCallback) {
                    gameEndCallback({
                      gameWon: false,
                      gamePhase: 'failed',
                      potionAttempts: gameCompletion?.previousAttempts || []
                    });
                  }
                }, 3000); // Wait for smoke effect duration
              }, 2000); // Short delay before smoke
              
            } else if (isWinningPotion) {
              console.log('🎉 GAME WON! Love potion created, triggering win scenario');
              
              // Trigger win scenario with screen smoke transition
              setTimeout(() => {
                console.log('🎭 Playing screen smoke for victory transition');
                playScreenSmokeEffect();
                
                // After smoke effect, trigger win scenario
                setTimeout(() => {
                  console.log('📤 Triggering win scenario event');
                  if (gameEndCallback) {
                    gameEndCallback({
                      gameWon: true,
                      gamePhase: 'completed',
                      potionAttempts: gameCompletion?.previousAttempts || []
                    });
                  }
                }, 3000); // Wait for smoke effect duration
              }, 2000); // Short delay before smoke
            }
          }; // Close the nextPhaseCallback function
          
          // Debug: Check queue status after AI commentary
          const queueStatus = eventQueue.getStatus();
          console.log('🔍 Queue status after AI commentary:', queueStatus);
          
          if (isWinningPotion && gameWon) {
            sendMessageWithContext(`react to the successful ${potionName} love potion`);
          } else if (result?.nameStyle === 'Near Miss Love') {
            sendMessageWithContext(`react to the near-miss ${potionName} potion result`);
          } else if (result?.nameStyle === 'Gross/Comedic') {
            sendMessageWithContext(`react to the disgusting ${potionName} potion result`);
          } else {
            sendMessageWithContext(`react to the ${potionName} potion result`);
          }
        });
        
        // Step 4: Wait for AI commentary completion, then handle attempt progression
        console.log(`⏰ Base sequence timing: smoke@5s, video@8s, commentary@14s, waiting for AI message completion...`);
        
        // Debug: Log current queue status after scheduling all events
        setTimeout(() => {
          const queueStatus = eventQueue.getStatus();
          console.log('🔍 Queue status after scheduling all events:', queueStatus);
        }, 100);
        
        // Log the full result for debugging immediately
        if (result) {
          console.log(`🧪 Potion completed: ${result.potionName} (${result.shape} ${result.color})`);
          console.log(`📝 Name Style: ${result.nameStyle}, Love Potion: ${isWinningPotion}`);
        }
      }
      
      // Handle narrative flow events
      else if (eventData?.data?.type === 'narrativeFlow') {
        const { action, ingredient } = eventData.data;
        console.log('Processing narrative flow event:', { action, ingredient });
        
        switch (action) {
          case 'game_start_introduction':
            console.log('Game started - introduction complete');
            // Initialize AttemptCounter to show attempt 1
            if (attemptCounterUpdateCallback) {
              console.log('🎯 Initializing AttemptCounter to show attempt 1/3');
              attemptCounterUpdateCallback(1, 3);
            }
            break;
            
          case 'comment_on_ingredient':
            console.log('AI commented on ingredient:', ingredient);
            break;
            
          case 'reject_ingredient_with_hint':
            console.log('AI rejected ingredient with hint:', ingredient);
            break;
            
          default:
            console.log('Unknown narrative flow action:', action);
        }
      }

      // Handle delayed trigger events
      else if (eventData?.data?.type === 'delayedTrigger') {
        const { nextAction, delay, potionResultData, potionCommentaryData } = eventData.data;
        console.log('Processing delayed trigger:', { nextAction, delay });
        
        eventQueue.enqueue(`delayed_${nextAction}`, delay || 0, () => {
          console.log('Executing delayed action:', nextAction);
          
          // SILENT actions - execute directly without sending to AI
          if (nextAction === 'deactivate_buttons') {
            controls.deactivateAllButtons();
          } else if (nextAction === 'activate_button1') {
            controls.activateButton1();
          } else if (nextAction === 'activate_button2') {
            controls.activateButton2();
          } else if (nextAction === 'activate_button3') {
            controls.activateButton3();
          } else if (nextAction === 'request_second_ingredient') {
            // Silent activation of button3 only
            controls.activateButton3();
          } else if (nextAction === 'request_third_ingredient') {
            // Silent activation of button1 only
            controls.activateButton1();
          } else if (nextAction === 'complete_sequence') {
            // This should trigger the backend complete_sequence action
            console.log('Complete sequence triggered - backend will handle this');
          } else if (nextAction === 'potionResult' && potionResultData) {
            // Process the potion result data as if it was a direct potionResult event
            console.log('Delayed potion result triggered:', potionResultData);
            handleSystemEvent({ data: potionResultData });
          } else if (nextAction === 'ai_potion_commentary' && potionCommentaryData) {
            // Trigger AI to make a funny comment about the potion
            console.log('AI potion commentary triggered:', potionCommentaryData);
            
            // Send a contextual message to trigger AI commentary
            const { potionName, isWinningPotion, nameStyle, gameWon } = potionCommentaryData;
            
            if (isWinningPotion && gameWon) {
              sendMessageWithContext(`react to the successful ${potionName} love potion`);
            } else if (nameStyle === 'Near Miss Love') {
              sendMessageWithContext(`react to the near-miss ${potionName} potion result`);
            } else if (nameStyle === 'Gross/Comedic') {
              sendMessageWithContext(`react to the disgusting ${potionName} potion result`);
            } else {
              sendMessageWithContext(`react to the ${potionName} potion result`);
            }
          } else {
            // Any other actions that should be sent to AI (if needed in the future)
            console.log('Unknown delayed action:', nextAction);
          }
        }, delay || 0);
      }

      // Handle attempt failure events - NEW GAME EVENT HANDLING
      else if (eventData?.data?.type === 'attemptFailed') {
        const { currentAttempt, maxAttempts, nextAttempt, delay } = eventData.data;
        console.log('🚨 Processing attempt failed event:', { currentAttempt, maxAttempts, nextAttempt, delay });
        console.log('🚨 Will clear buttons in', delay, 'milliseconds');
        
        // Schedule button clearing and next attempt preparation after delay
        eventQueue.enqueue('clear_buttons_for_next_attempt', delay || 0, () => {
          console.log(`🧹 Attempt ${currentAttempt} failed - clearing buttons for attempt ${nextAttempt}`);
          
          // Clear all button texts
          controls.updateButtonText('button1', '');
          controls.updateButtonText('button2', '');
          controls.updateButtonText('button3', '');
          
          // Deactivate all buttons
          controls.deactivateAllButtons();
          
          console.log(`✅ UI prepared for attempt ${nextAttempt}/${maxAttempts}`);
        }, delay || 0);
      }

      // Handle attempt started events - Update UI counter
      else if (eventData?.data?.type === 'attemptStarted') {
        const { currentAttempt, maxAttempts } = eventData.data;
        console.log(`🚫 Ignoring backend attemptStarted event (attempt ${currentAttempt}/${maxAttempts}) - using frontend-controlled attempt tracking`);
        
        // NOTE: We're now managing attempt counter updates manually in the next_phase handler
        // to avoid conflicts between frontend calculation and backend auto-increment
        // Directly update AttemptCounter if callback is registered
        // if (attemptCounterUpdateCallback) {
        //   attemptCounterUpdateCallback(currentAttempt, maxAttempts);
        // }
      }
      
      // Handle traditional potion game events too
      else if (eventData?.data?.type === 'potionGameAction') {
        const { action, ingredient } = eventData.data;
        console.log('Processing traditional potion game action:', { action, ingredient });
        
        switch (action) {
          case 'start_game':
            //potionGame.gameState.startNewGame();
            break;
          case 'reset_game':
            //potionGame.resetGame();
            break;
          case 'add_ingredient':
            if (ingredient) {
              //potionGame.gameState.addIngredient({
                //name: ingredient,
                //category: 'Herb/Fresh', // Default category, will be classified later
                //rawInput: ingredient
             // });
            }
            break;
          // Add other traditional game actions as needed
        }
      }
    };

    // Subscribe to system_event messages from the backend
    sub('system_event', handleSystemEvent);
    
    // Subscribe to message events to track AI completion
    sub('message', handleMessage);

    // Cleanup function
    return () => {
      unsub('system_event', handleSystemEvent);
      unsub('message', handleMessage);
      // Cancel all pending events to prevent memory leaks and unwanted actions
      eventQueue.cancelAll();
      console.log('🧹 AI-Potion UI Bridge cleanup completed');
    };
  }, [, sub, unsub]);

  return {controls, activeButton, buttonTexts, handleButtonToggle };
};
